import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { Search } from "lucide-react";
import { cn } from "@/modern/utils";
import SpotIncentiveDateRangePicker from "./SpotIncentiveDateRangePicker.jsx";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { getEligibleResourceForSpotIncentive } from "@/other/utils/incentiveUtils.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { SpotIncentivePickerShell, type SpotIncentivePickerPresentation } from "./SpotIncentivePickerShell";

interface ResourceSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedResourceIds: number[];
  onResourcesSelect: (data: {
    resourceIds: number[];
    startDate: string;
    endDate: string;
    multiplyByDays: boolean;
  }) => void;
  selectedTaskId: number | null;
  editorInstance?: unknown;
  multiplyByDays: boolean;
  presentation?: SpotIncentivePickerPresentation;
}

const SpotIncentiveResourceSelector = ({
  open,
  onOpenChange,
  selectedResourceIds,
  onResourcesSelect,
  selectedTaskId,
  multiplyByDays,
  presentation = "dialog",
}: ResourceSelectorProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { project } = useContext(BryntumContext) as any;
  const [searchQuery, setSearchQuery] = useState("");
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>(selectedResourceIds);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [localMultiplyByDays, setLocalMultiplyByDays] = useState<boolean>(multiplyByDays);
  const eventRecordMap = useRef(new Map());

  // Timeline dates calculation like ResourceStep
  const timelineDates = useMemo(() => {
    const now = new Date();
    const timelineStartDate = new Date(now);
    timelineStartDate.setMonth(timelineStartDate.getMonth() - 3);
    const timelineEndDate = new Date(now);
    return { timelineStartDate, timelineEndDate };
  }, []);

  const handleDateRangeChange = (selectedStartDate: Date, selectedEndDate: Date) => {
    setStartDate(selectedStartDate.toISOString());
    setEndDate(selectedEndDate.toISOString());
  };

  // Get eligible resources based on task and date range
  const eligibleResources = useMemo(() => {
    if (!selectedTaskId || !project || !startDate || !endDate) return [];

    try {
      const events = getEligibleResourceForSpotIncentive({
        taskId: selectedTaskId,
        project,
        startDate: new Date(startDate),
        endDate: DateHelper.add(endDate, 1, "day")
      });

      // Update eventRecordMap
      events.forEach(({ record, timeSpent }) =>
        eventRecordMap.current.set(Number(record.resourceId), { record, timeSpent })
      );

      return events.map(({ record }) => ({
        resourceId: Number(record.resourceId), // Ensure integer
        name: record.resource?.name || 'Unknown',
        resource: record.resource
      }));
    } catch (error) {
      return [];
    }
  }, [selectedTaskId, project, startDate, endDate]);

  // Update local state when dialog opens
  useEffect(() => {
    if (open && eligibleResources.length > 0) {
      // Auto-select all eligible resources if none selected
      const autoSelectedIds = selectedResourceIds.length === 0
        ? eligibleResources.map(r => r.resourceId)
        : selectedResourceIds;
      setLocalSelectedIds(autoSelectedIds);
      setLocalMultiplyByDays(multiplyByDays);
    } else if (open) {
      setLocalSelectedIds(selectedResourceIds);
      setLocalMultiplyByDays(multiplyByDays);
    }
  }, [open, selectedResourceIds, eligibleResources, multiplyByDays]);

  // Update local state when dialog opens or closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset local state when closing without saving
      setLocalSelectedIds(selectedResourceIds);
      setLocalMultiplyByDays(multiplyByDays);
    }
    onOpenChange(isOpen);
  };

  const filteredResources = eligibleResources.filter(resource =>
    resource.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleResourceToggle = (resourceId: number) => {
    const newSelection = localSelectedIds.includes(resourceId)
      ? localSelectedIds.filter(id => id !== resourceId)
      : [...localSelectedIds, resourceId];

    setLocalSelectedIds(newSelection);
  };

  const handleSave = () => {
    onResourcesSelect({
      resourceIds: localSelectedIds,
      startDate: startDate || new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      endDate: endDate || new Date().toISOString(),
      multiplyByDays: localMultiplyByDays
    });
    onOpenChange(false);
  };

  return (
    <SpotIncentivePickerShell
      open={open}
      onClose={() => handleOpenChange(false)}
      presentation={presentation}
      title="Select Resources"
      description="Pick resources by date range on this task"
      size={{
        width: "48rem",
        height: "46rem",
        minWidth: "min(90vw, 48rem)",
        minHeight: "min(90vh, 46rem)",
        maxWidth: "90vw",
        maxHeight: "90vh",
      }}
      dialogClassName="min-h-[min(46rem,85vh)]"
      footer={
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            className="h-9 rounded-full px-3 text-sm font-normal"
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} className="h-9 rounded-full px-3 text-sm font-normal">
            Done
          </Button>
        </div>
      }
    >
        <div className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="shrink-0 space-y-2">
            <SpotIncentiveDateRangePicker
              timelineStartDate={timelineDates.timelineStartDate}
              timelineEndDate={timelineDates.timelineEndDate}
              selectedStartDate={startDate ? new Date(startDate) : null}
              selectedEndDate={endDate ? new Date(endDate) : null}
              onChange={handleDateRangeChange}
            />
          </div>

          <div className="relative shrink-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter resources…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 rounded-full border-input bg-muted pl-9 text-sm shadow-none"
            />
          </div>

          <ScrollArea className={cn("flex-1", presentation === "inline" ? "min-h-0" : "min-h-[12rem]")}>
            <div className="space-y-1.5 pr-3">
              <p className="text-sm text-muted-foreground">
                Eligible ({filteredResources.length})
              </p>
              {filteredResources.length > 0 ? (
                filteredResources.map((resource) => {
                  const eventData = eventRecordMap.current.get(resource.resourceId);
                  const timeSpent = eventData?.timeSpent || 0;
                  const daysWorked = timeSpent % 1 === 0 ? timeSpent : timeSpent.toFixed(2);
                  const selected = localSelectedIds.includes(resource.resourceId);
                  return (
                    <button
                      key={resource.resourceId}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                        selected
                          ? "border-primary/40 bg-primary/10"
                          : "border-border/50 hover:bg-muted/40",
                      )}
                      onClick={() => handleResourceToggle(resource.resourceId)}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => handleResourceToggle(resource.resourceId)}
                      />
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <span className="text-sm font-normal">{resource.name}</span>
                        <span className="text-sm text-muted-foreground tabular-nums">
                          {daysWorked} day{daysWorked != 1 ? "s" : ""}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : selectedTaskId ? (
                <div className="rounded-xl border border-dashed border-border/60 p-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    No resources found for this task and date range.
                  </p>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border/60 p-6 text-center">
                  <p className="text-sm text-muted-foreground">Select a task first.</p>
                </div>
              )}
            </div>
          </ScrollArea>

          <div
            className="flex shrink-0 cursor-pointer items-start gap-3 rounded-2xl border border-border/50 bg-muted/30 p-4 transition-colors hover:bg-muted/50"
            onClick={() => setLocalMultiplyByDays(!localMultiplyByDays)}
          >
            <Checkbox
              id="multiply-by-days"
              checked={localMultiplyByDays}
              onCheckedChange={(checked) => setLocalMultiplyByDays(checked === true)}
            />
            <div className="flex-1 space-y-1">
              <div className="text-sm font-normal leading-none">Multiply points by days-on-task</div>
              <p className="text-xs text-muted-foreground">
                Points are multiplied by each resource&apos;s days on task within the selected range.
              </p>
            </div>
          </div>
        </div>
    </SpotIncentivePickerShell>
  );
};

export default SpotIncentiveResourceSelector;
